/**
 * Client unique de l'API `/api/**` : ajoute `Authorization` et
 * `X-Firebase-AppCheck`, traduit les erreurs JSON en messages affichables (RG-73).
 */
import { getToken } from 'firebase/app-check'
import { appCheck, auth } from '@/firebase'
import { fromLoanJSON, toDate } from '@/services/converters'
import type {
  BookCandidate,
  CardExtraction,
  Loan,
  PublicBook,
  PublicStats,
  Role,
  UserSummary,
} from '@/types'

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

const NETWORK_MESSAGE = 'Connexion impossible. Vérifiez votre accès à Internet, puis réessayez.'
const GENERIC_MESSAGE = 'Une erreur inattendue est survenue. Réessayez dans un instant.'

/** Message affichable pour n'importe quelle erreur. */
export function errorMessage(e: unknown): string {
  if (e instanceof ApiError) return e.message
  return GENERIC_MESSAGE
}

interface CallOptions {
  method?: 'GET' | 'POST'
  body?: unknown
  authenticated?: boolean
}

async function call<T>(path: string, { method = 'GET', body, authenticated = true }: CallOptions = {}): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' }
  if (body !== undefined) headers['Content-Type'] = 'application/json'

  if (authenticated) {
    const user = auth.currentUser
    if (!user) throw new ApiError(401, 'UNAUTHENTICATED', 'Vous devez être connecté pour effectuer cette action.')
    headers.Authorization = `Bearer ${await user.getIdToken()}`
    if (appCheck) {
      try {
        headers['X-Firebase-AppCheck'] = (await getToken(appCheck, false)).token
      } catch {
        throw new ApiError(401, 'APP_CHECK_FAILED', 'La requête n’a pas pu être vérifiée. Rechargez la page et réessayez.')
      }
    }
  }

  let res: Response
  try {
    res = await fetch(path, { method, headers, body: body === undefined ? undefined : JSON.stringify(body) })
  } catch {
    throw new ApiError(0, 'NETWORK', NETWORK_MESSAGE)
  }

  if (!res.ok) {
    let code = 'HTTP_' + res.status
    let message = GENERIC_MESSAGE
    try {
      const data = (await res.json()) as { error?: { code?: string; message?: string } }
      if (data.error?.code) code = data.error.code
      if (data.error?.message) message = data.error.message
    } catch {
      // Réponse non JSON (proxy, page d'erreur) : message générique.
    }
    throw new ApiError(res.status, code, message)
  }
  return (await res.json()) as T
}

// --- Vitrine (RG-10 à RG-14) -------------------------------------------------

interface RawPublicBook extends Omit<PublicBook, 'createdAt'> {
  createdAt: string
}

export async function fetchPublicBooks(): Promise<{ books: PublicBook[]; stats: PublicStats }> {
  // Sans authentification : la réponse est mise en cache par le CDN.
  const data = await call<{ books: RawPublicBook[]; stats: PublicStats }>('/api/public/books', { authenticated: false })
  return {
    books: data.books.map((b) => ({ ...b, createdAt: toDate(b.createdAt) ?? new Date(0) })),
    stats: data.stats,
  }
}

// --- Ajout d'un livre (RG-41 à RG-49) ----------------------------------------

export function extractCard(tmpPath: string): Promise<CardExtraction> {
  return call<CardExtraction>('/api/cards/extract', { method: 'POST', body: { tmpPath } })
}

export interface SearchQuery {
  title?: string
  author?: string
  isbn?: string
  /** Texte libre (« Titre, auteur ou ISBN » de la saisie manuelle). */
  q?: string
}

export async function searchBooks(query: SearchQuery): Promise<BookCandidate[]> {
  const data = await call<{ candidates: BookCandidate[] }>('/api/books/search', { method: 'POST', body: query })
  return data.candidates
}

export async function commitCard(tmpPath: string, bookId: string): Promise<string> {
  const data = await call<{ cardImagePath: string }>('/api/cards/commit', { method: 'POST', body: { tmpPath, bookId } })
  return data.cardImagePath
}

// --- Emprunt et retour (RG-30 à RG-34) ---------------------------------------

export async function borrowBook(bookId: string, externalBorrowerName?: string): Promise<Loan> {
  const body = externalBorrowerName ? { bookId, externalBorrowerName } : { bookId }
  return fromLoanJSON(await call<Record<string, unknown>>('/api/loans/borrow', { method: 'POST', body }))
}

export async function returnBook(bookId: string): Promise<Loan> {
  return fromLoanJSON(await call<Record<string, unknown>>('/api/loans/return', { method: 'POST', body: { bookId } }))
}

// --- Administration (RG-60 à RG-62) ------------------------------------------

interface RawUser extends Omit<UserSummary, 'createdAt'> {
  createdAt: string
}
const toUser = (u: RawUser): UserSummary => ({ ...u, createdAt: toDate(u.createdAt) ?? new Date(0) })

export async function listUsers(): Promise<UserSummary[]> {
  const data = await call<{ users: RawUser[] }>('/api/admin/users')
  return data.users.map(toUser)
}

export async function setUserRole(uid: string, role: Exclude<Role, 'superadmin'>): Promise<UserSummary> {
  return toUser(await call<RawUser>(`/api/admin/users/${encodeURIComponent(uid)}/role`, { method: 'POST', body: { role } }))
}

/** Export complet JSON (NFR portabilité), renvoyé tel quel pour téléchargement. */
export async function exportAll(): Promise<Blob> {
  const data = await call<unknown>('/api/admin/export')
  return new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
}
